import { useEffect, useRef, useState } from "react";

import { formatCountdown } from "@/hooks/useTestCountdown";

interface TestCountdownProps {
  secondsRemaining: number;
  /** Hides the urgency styling for a practice run. */
  muted?: boolean;
  /** Small enough for a list row. The exam itself uses the large one. */
  compact?: boolean;
}

// Thresholds at which the remaining time is announced to a screen
// reader. Announcing every tick would be unusable; these are the points
// a candidate actually wants to know about.
const ANNOUNCE_AT = [600, 300, 60];

/**
 * The exam clock.
 *
 * Accessibility note: the ticking value is `aria-live="off"` on purpose.
 * A polite live region on a per-second counter floods a screen reader
 * with the same number and drowns out everything else on the page. The
 * milestones are announced separately, which is the information a
 * candidate actually needs.
 */
export function TestCountdown({
  secondsRemaining,
  muted,
  compact,
}: TestCountdownProps) {
  const [announcement, setAnnouncement] = useState("");
  const announcedRef = useRef<Set<number>>(new Set());
  const armedRef = useRef(false);

  useEffect(() => {
    // The countdown reads 0 until the attempt loads. Announcing against
    // that zero burns every threshold at once -- a screen-reader user was
    // told "5 minutes remaining" on a one-hour test, and then never
    // warned again, because 10/5/1 had all been marked as already said.
    //
    // So: arm on the first real value, and mark anything already behind
    // us as spent WITHOUT announcing it. Starting a four-minute test
    // should not immediately read out "10 minutes remaining" either.
    if (secondsRemaining <= 0) return;

    if (!armedRef.current) {
      armedRef.current = true;
      for (const threshold of ANNOUNCE_AT) {
        if (secondsRemaining <= threshold) announcedRef.current.add(threshold);
      }
      return;
    }

    for (const threshold of ANNOUNCE_AT) {
      if (secondsRemaining <= threshold && !announcedRef.current.has(threshold)) {
        announcedRef.current.add(threshold);
        const minutes = Math.round(threshold / 60);
        setAnnouncement(
          `${minutes} minute${minutes === 1 ? "" : "s"} remaining`,
        );
        break;
      }
    }
  }, [secondsRemaining]);

  const urgent = !muted && secondsRemaining <= 60;
  const warning = !muted && !urgent && secondsRemaining <= 300;

  // Colours come from theme tokens, never hard-coded hex — the backend
  // contrast tests police the token set in both themes.
  const tone = urgent
    ? "bg-status-urgent-bg text-status-urgent"
    : warning
      ? "bg-status-wip-bg text-status-wip"
      : "bg-background-card text-foreground";

  return (
    <>
      <div
        role="timer"
        aria-live="off"
        data-cy="test-countdown"
        data-urgent={urgent ? "true" : undefined}
        className={[
          "rounded-[10px] border font-mono font-bold tabular-nums leading-none",
          // The exam clock is large on purpose -- a candidate budgets an
          // hour of their own time against it and must read it at a
          // glance. 34px in a plate, per the design: the older 5xl was
          // nearly twice that and made the label beside it read as a
          // caption.
          compact
            ? "border-border px-3 py-1.5 text-lg"
            : "border-border bg-background-subtle px-4 py-1.5 text-[34px] tracking-[-0.02em]",
          urgent && !compact ? "border-status-urgent" : "border-border",
          tone,
        ].join(" ")}
        title="Time remaining"
      >
        {/* NO "remaining" SUFFIX. The header names the readout above it,
            and saying it twice is what made the group look unstructured. */}
        {formatCountdown(secondsRemaining)}
      </div>
      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </>
  );
}
